import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import MobileLayout, { SwitchToDesktopLink } from '../MobileLayout';
import { apiGet } from '../api';
import { MobileCustomer } from '../types';

const formatRelative = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return '';
  const diff = Date.now() - d;
  const day = 24 * 60 * 60 * 1000;
  if (diff < day) return 'today';
  if (diff < 2 * day) return 'yesterday';
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  if (diff < 30 * day) return `${Math.floor(diff / (7 * day))}w ago`;
  return new Date(iso).toLocaleDateString();
};

const TypeTag: React.FC<{ type?: string }> = ({ type }) => {
  if (!type) return null;
  const color =
    type === 'customer' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200' :
    type === 'prospect' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200' :
    'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${color}`}>{type}</span>;
};

const MobileCustomers: React.FC = () => {
  const [customers, setCustomers] = useState<MobileCustomer[]>([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    apiGet<any[]>('/customers')
      .then((data) => {
        if (cancel) return;
        const mapped: MobileCustomer[] = data.map((c) => ({
          id: c.id,
          name: c.name,
          type: c.type,
          company: c.company,
          projectCount: c.projectCount ?? c.projects_count ?? 0,
          reportCount: c.reportCount ?? 0,
          lastActivity: c.lastActivity ?? c.updatedAt ?? null,
        }));
        setCustomers(mapped);
      })
      .catch((e) => !cancel && setError(e instanceof Error ? e.message : 'Failed to load customers'))
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, []);

  const filtered = customers.filter((c) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (
      (c.company || '').toLowerCase().includes(q) ||
      (c.name || '').toLowerCase().includes(q)
    );
  });

  // Sort: company A→Z primarily, name as tiebreaker
  filtered.sort((a, b) =>
    (a.company || a.name || '').localeCompare(b.company || b.name || '', undefined, { sensitivity: 'base' })
  );

  return (
    <MobileLayout title="Customers" topRight={<SwitchToDesktopLink />}>
      <div className="sticky top-0 z-10 bg-slate-50 px-3 pb-2 pt-3 dark:bg-slate-950">
        <input
          type="search"
          inputMode="search"
          placeholder="Search customers…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
        />
      </div>

      {loading && <div className="px-3 py-6 text-center text-sm text-slate-500">Loading…</div>}
      {error && <div className="mx-3 my-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/30 dark:text-red-200">{error}</div>}

      <ul className="divide-y divide-slate-100 bg-white dark:divide-slate-800 dark:bg-slate-900">
        {filtered.map((c) => (
          <li key={c.id}>
            <Link
              to={`/m/customers/${c.id}`}
              className="flex items-center justify-between gap-3 px-3 py-3 active:bg-slate-50 dark:active:bg-slate-800"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                    {c.company || c.name || 'Untitled'}
                  </div>
                  <TypeTag type={c.type} />
                </div>
                {c.company && c.name && (
                  <div className="truncate text-xs text-slate-500 dark:text-slate-400">{c.name}</div>
                )}
                <div className="mt-0.5 text-[11px] text-slate-400">
                  {c.projectCount ?? 0} {(c.projectCount ?? 0) === 1 ? 'project' : 'projects'}
                  {c.lastActivity ? ` · ${formatRelative(c.lastActivity)}` : ''}
                </div>
              </div>
              <span className="text-slate-300">›</span>
            </Link>
          </li>
        ))}
        {!loading && filtered.length === 0 && (
          <li className="px-3 py-8 text-center text-sm text-slate-500">No customers match your search.</li>
        )}
      </ul>
    </MobileLayout>
  );
};

export default MobileCustomers;
